import cn from 'clsx'
import { FC, useState } from 'react'

import { Button, Typography } from '@/components/ui'

import Check from '@/assets/icons/check.svg'
import Plus from '@/assets/icons/plus2.svg'

import { IHighlightsFolder } from '@/types/highlight.interface'

import FolderColorBalls from './FolderColorBalls'

interface ISelectedSegmentFolderMenuProps {
	folders: IHighlightsFolder[]
	highlightMenu: {
		x: number
		y: number
		strategy: 'absolute' | 'fixed'
	}
	onSave: (folderIds: number[]) => void
	onCreateFolder: () => void
}

const SelectedSegmentFolderMenu: FC<ISelectedSegmentFolderMenuProps> = ({
	folders,
	highlightMenu,
	onSave,
	onCreateFolder
}) => {
	const [selectedFolderIds, setSelectedFolderIds] = useState<number[]>([])

	const toggleFolder = (folderId: number) => {
		setSelectedFolderIds(prev =>
			prev.includes(folderId)
				? prev.filter(id => id !== folderId)
				: [...prev, folderId]
		)
	}

	return (
		<div
			onMouseDown={event => event.preventDefault()}
			style={{
				left: highlightMenu?.x,
				top: highlightMenu?.y,
				transform: 'translateX(-50%)'
			}}
			className={cn(
				'z-50 w-[310px] rounded-[12px] bg-white shadow-[0_8px_24px_rgba(0,0,0,0.16)] overflow-hidden',
				highlightMenu.strategy === 'fixed' ? 'fixed' : 'absolute'
			)}
		>
			<div className='flex items-center justify-between px-4 py-3 border-b border-neutral-100'>
				<Typography variant='text' className='font-medium text-neutral-900'>
					Add to folder
				</Typography>

				<Button
					className='rounded-[8px] hover:bg-neutral-200'
					classNameText='flex items-center gap-2 text-neutral-900'
					onClick={onCreateFolder}
				>
					<Plus className='w-4 h-4' />
					<span>New folder</span>
				</Button>
			</div>

			<div className='max-h-[260px] overflow-y-auto'>
				{folders.map(folder => {
					const isChecked = selectedFolderIds.includes(folder.id)

					return (
						<div
							key={folder.id}
							role='checkbox'
							aria-checked={isChecked}
							tabIndex={0}
							onClick={() => toggleFolder(folder.id)}
							onKeyDown={event => {
								if (event.key === 'Enter' || event.key === ' ') {
									event.preventDefault()
									toggleFolder(folder.id)
								}
							}}
							className={cn(
								'flex h-9 cursor-pointer items-center gap-3 px-4 hover:bg-neutral-100',
								isChecked && 'bg-neutral-100'
							)}
						>
							<input
								type='checkbox'
								checked={isChecked}
								readOnly
								tabIndex={-1}
								className='h-3.5 w-3.5 accent-neutral-900 pointer-events-none'
							/>

							<div className='flex flex-col gap-[2px]'>
								<FolderColorBalls colorCode={folder.color_code} />
							</div>

							<Typography variant='text' className='truncate text-neutral-900'>
								{folder.name}
							</Typography>
						</div>
					)
				})}
			</div>

			<div className='p-3 border-t border-neutral-100'>
				<Button
					disabled={!selectedFolderIds.length}
					className='w-full rounded-[8px] bg-neutral-200 hover:bg-neutral-300'
					classNameText='flex items-center gap-2 text-neutral-900'
					onClick={() => onSave(selectedFolderIds)}
				>
					<Check className='w-4 h-4 text-neutral-900' />
					<span>Save changes</span>
				</Button>
			</div>
		</div>
	)
}

export default SelectedSegmentFolderMenu
